import { useEffect, useRef, useState, useCallback } from "react";
import { ref, onValue } from "firebase/database";
import { database } from "../../../../config/firebaseConfig";

/* ============================================================
   VodGalleryDisplay — 1280x800 "Watch Shows" gallery (touch)
   PASS 2 (parity with TV pass + touch player controls):
   - Static art: /vodhero.jpg, /vodthumb.jpg, /brickdisplay.jpg
   - Marquee bulbs aligned to hero border
   - Cards: image + date plate below; row excludes the hero show
   - Player: iframe is pointerEvents:none; React owns controls:
       tap screen -> controls appear (big center play/pause,
       -30/+30, finger-scrubbable progress bar, volume -/+, EXIT)
       auto-hide 3s while playing; stay while paused
   - Requires player5.html bridge with toggle/seek/seekto/vol + vodtime
   ============================================================ */

const PLAYER = "https://vod.mywaitserver.live/player5.html";
const HERO_IMG = "/vodhero.jpg";
const THUMB_IMG = "/vodthumb.jpg";
const CHASE_DUR = 1.1;
const SKIP_SEC = 30;

function fmtDate(iso) {
  const p = iso.split("-");
  const d = new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]));
  const days = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const mon = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  return { weekday: days[d.getDay()], monthDay: mon[d.getMonth()] + " " + d.getDate(), year: d.getFullYear() };
}
const chaseDelay = (i) => (-((i % 3) * (CHASE_DUR / 3))).toFixed(3) + "s";

function fmtClock(sec) {
  sec = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s2 = sec % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s2).padStart(2, "0");
  return h > 0 ? h + ":" + mm + ":" + ss : m + ":" + ss;
}

/* ---- resume-from-last-position (localStorage, front-end only) ---- */
const HIST_KEY = (date) => `watchHistory_${date}`;
const NEAR_END_SEC = 300;
const MIN_RESUME_SEC = 30;

function readHistory(date) {
  try {
    const raw = localStorage.getItem(HIST_KEY(date));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj.pos !== "number") return null;
    return obj;
  } catch (e) { return null; }
}
function writeHistory(date, pos) {
  try { localStorage.setItem(HIST_KEY(date), JSON.stringify({ pos, at: Date.now() })); } catch (e) {}
}
function clearHistory(date) {
  try { localStorage.removeItem(HIST_KEY(date)); } catch (e) {}
}

const VodGalleryDisplay = ({ onExit, onVideoActive }) => {
  const [shows, setShows] = useState([]);
  const [playerSrc, setPlayerSrc] = useState(null);
  const [bulbs, setBulbs] = useState([]);
  // player state
  const [isPaused, setIsPaused] = useState(false);
  const [vodTime, setVodTime] = useState({ cur: 0, dur: 0 });
  const [showCtl, setShowCtl] = useState(true);
  const [vol, setVol] = useState(60);
  const [dragT, setDragT] = useState(null); // while finger-scrubbing
  const [skipFlash, setSkipFlash] = useState(null);
  // resume feature
  const [currentShow, setCurrentShow] = useState(null);
  const [resumePrompt, setResumePrompt] = useState(null); // { show, pos }
  const pendingSeekRef = useRef(null);
  const vodTimeRef = useRef({ cur: 0, dur: 0 });
  const dragTRef = useRef(null);
  const heroRef = useRef(null);
  const playerFrameRef = useRef(null);
  const ctlTimerRef = useRef(null);
  const skipTimerRef = useRef(null);

  // ---- live listener: shows update instantly when RTDB changes ----
  useEffect(() => {
    const showsRef = ref(database, "vodShows");
    const unsub = onValue(
      showsRef,
      (snap) => {
        const data = snap.val();
        if (!data) { setShows([]); return; }
        const list = Object.keys(data).map((k) => data[k]);
        list.sort((a, b) => (a.date < b.date ? 1 : -1));
        setShows(list);
      },
      () => {} // on error: keep whatever we have
    );
    return () => unsub();
  }, []);

  // ---- marquee bulbs aligned to hero border ----
  const computeBulbs = useCallback(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const ox = hero.offsetLeft, oy = hero.offsetTop;
    const w = hero.offsetWidth, h = hero.offsetHeight;
    const x0 = ox, y0 = oy, x1 = ox + w, y1 = oy + h;
    const sp = 24;
    const pts = [];
    const edge = (ax, ay, bx, by) => {
      const len = Math.hypot(bx - ax, by - ay);
      const n = Math.max(1, Math.round(len / sp));
      for (let i = 0; i < n; i++) {
        const t = i / n;
        pts.push([ax + (bx - ax) * t, ay + (by - ay) * t]);
      }
    };
    edge(x0, y0, x1, y0); edge(x1, y0, x1, y1); edge(x1, y1, x0, y1); edge(x0, y1, x0, y0);
    setBulbs(pts.map((p, i) => ({ x: p[0], y: p[1], delay: chaseDelay(i) })));
  }, []);

  useEffect(() => {
    if (!shows.length) return;
    const t = setTimeout(computeBulbs, 60);
    const onResize = () => computeBulbs();
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(t); window.removeEventListener("resize", onResize); };
  }, [shows, computeBulbs]);

  // ---- receive time from player ----
  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev && ev.data;
      if (d && d.vodtime) {
        setVodTime(d.vodtime);
        vodTimeRef.current = d.vodtime;
        if (pendingSeekRef.current != null && d.vodtime.dur > 0) {
          const t = pendingSeekRef.current;
          pendingSeekRef.current = null;
          try {
            playerFrameRef.current?.contentWindow?.postMessage({ vodctl: "seekto", t }, "*");
          } catch (e) {}
        }
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // save position every 30s while a show is open
  useEffect(() => {
    if (!playerSrc || !currentShow) return;
    const iv = setInterval(() => {
      const t = vodTimeRef.current;
      if (t.dur > 0 && t.cur > MIN_RESUME_SEC) writeHistory(currentShow.date, t.cur);
    }, 30000);
    return () => clearInterval(iv);
  }, [playerSrc, currentShow]);

  const sendToPlayer = useCallback((msg) => {
    try { playerFrameRef.current?.contentWindow?.postMessage(msg, "*"); } catch (e) {}
  }, []);

  // ---- controls visibility: reset hide timer on any interaction ----
  const pokeCtl = useCallback((pausedNow) => {
    setShowCtl(true);
    if (ctlTimerRef.current) clearTimeout(ctlTimerRef.current);
    const paused = pausedNow !== undefined ? pausedNow : isPaused;
    if (!paused) {
      ctlTimerRef.current = setTimeout(() => {
        if (dragTRef.current === null) setShowCtl(false); // never hide mid-drag
      }, 3000);
    }
  }, [isPaused]);

  useEffect(() => () => {
    if (ctlTimerRef.current) clearTimeout(ctlTimerRef.current);
    if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
  }, []);

  const flashSkip = useCallback((dir) => {
    setSkipFlash({ dir, id: Date.now() });
    if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
    skipTimerRef.current = setTimeout(() => setSkipFlash(null), 800);
  }, []);

  const togglePlay = () => {
    sendToPlayer({ vodctl: "toggle" });
    setIsPaused((p) => { const np = !p; pokeCtl(np); return np; });
  };
  const skip = (dir) => {
    sendToPlayer({ vodctl: "seek", delta: dir * SKIP_SEC });
    flashSkip(dir);
    pokeCtl();
  };
  const changeVol = (delta) => {
    setVol((v) => {
      const nv = Math.max(0, Math.min(100, v + delta));
      sendToPlayer({ vodctl: "vol", v: nv });
      return nv;
    });
  };
  const updateDrag = (t) => {
    dragTRef.current = t;
    setDragT(t);
    // dragging keeps controls alive — kill any pending hide
    if (ctlTimerRef.current) clearTimeout(ctlTimerRef.current);
    setShowCtl(true);
  };

  const commitSeek = (t) => {
    sendToPlayer({ vodctl: "seekto", t });
    // hold the bar at the target while the seek lands (ignore snap-back)
    dragTRef.current = t;
    setDragT(t);
    setTimeout(() => { dragTRef.current = null; setDragT(null); }, 1200);
    pokeCtl();
  };

  const startShow = (show, fromPos) => {
    setIsPaused(false);
    setVodTime({ cur: 0, dur: 0 });
    vodTimeRef.current = { cur: 0, dur: 0 };
    setShowCtl(true);
    setDragT(null);
    dragTRef.current = null;
    setCurrentShow(show);
    pendingSeekRef.current = fromPos && fromPos > 0 ? fromPos : null;
    onVideoActive?.(true);
    setPlayerSrc(show.master || show.res720); // Display = ABR off master, 720-capped in player5
    if (ctlTimerRef.current) clearTimeout(ctlTimerRef.current);
    ctlTimerRef.current = setTimeout(() => {
      if (dragTRef.current === null) setShowCtl(false);
    }, 3000);
  };

  const openPlayer = (show) => {
    const hist = readHistory(show.date);
    if (hist && hist.pos > MIN_RESUME_SEC) {
      setResumePrompt({ show, pos: hist.pos });
    } else {
      startShow(show, null);
    }
  };

  const closePlayer = () => {
    const t = vodTimeRef.current;
    if (currentShow && t.dur > 0) {
      if (t.cur >= t.dur - NEAR_END_SEC) clearHistory(currentShow.date);
      else if (t.cur > MIN_RESUME_SEC) writeHistory(currentShow.date, t.cur);
      else clearHistory(currentShow.date);
    }
    setPlayerSrc(null);
    setCurrentShow(null);
    pendingSeekRef.current = null;
    onVideoActive?.(false);
    // restore default volume for the next open
    setVol(60);
  };

  // when the show ends, return to the gallery automatically
  useEffect(() => {
    if (!playerSrc) return;
    if (vodTime.dur > 0 && vodTime.cur >= vodTime.dur - 1) {
      closePlayer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vodTime, playerSrc]);

  if (!shows.length) {
    return (
      <div style={S.stage}>
        <div style={S.header}>
          <div><span style={S.wts}>WAIT TIME SIGNS</span><span style={S.live}>Live!</span></div>
        </div>
        <div style={S.empty}>
          <h2 style={S.emptyH}>The stage is quiet</h2>
          <p>No performances yet.</p>
        </div>
      </div>
    );
  }

  const latest = shows[0];
  const lf = fmtDate(latest.date);
  const barCur = dragT !== null ? dragT : vodTime.cur;

  return (
    <>
      <div style={S.amber} />
      <div style={S.stage}>
        <div style={S.header}>
          <div className="vodNeon">
            <span style={S.wts}>WAIT TIME SIGNS</span><span style={S.live}>Live!</span>
          </div>
        </div>

        {/* HERO */}
        <div style={S.heroWrap}>
          <section ref={heroRef} style={S.hero}>
            <div style={{ ...S.heroBg, backgroundImage: `url('${HERO_IMG}')` }} />
            <div style={S.heroScrim} />
            <div style={S.heroInner}>
              <span style={S.eyebrow}>Most Recent</span>
              <h1 style={S.heroH1}>
                {lf.weekday} {lf.monthDay}, <span style={{ color: "#f7c04a" }}>{lf.year}</span>
              </h1>
              <button onClick={() => openPlayer(latest)} style={S.btnWatch}>
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
                  <path d="M8 5v14l11-7z" />
                </svg>
                WATCH NOW!
              </button>
            </div>
          </section>
          <div style={S.heroMarquee}>
            {bulbs.map((b, i) => (
              <span key={i} className="vodBulb" style={{ ...S.bulb, left: b.x, top: b.y, animationDelay: b.delay }} />
            ))}
          </div>
        </div>

        {/* PAST PERFORMANCES (excludes hero show) */}
        {shows.length > 1 && (
          <section style={S.past}>
            <h2 style={S.pastH2}>Past Performances</h2>
            <div style={S.row}>
              {shows.slice(1).map((s) => {
                const f = fmtDate(s.date);
                return (
                  <div key={s.date} role="button" onClick={() => openPlayer(s)} style={S.card}>
                    <div style={{ ...S.cardImg, backgroundImage: `url('${THUMB_IMG}')` }} />
                    <div style={S.cardPlate}>{f.monthDay}, {f.year}</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* EXIT */}
        <button onClick={() => onExit?.()} style={S.exitBtn}>
          <span style={{ color: "white" }}>EXIT</span>
        </button>
      </div>

      {/* RESUME PROMPT */}
      {resumePrompt && (
        <div style={S.resumeWrap}>
          <div style={S.resumeBox}>
            <div style={S.resumeTitle}>Pick up where you left off?</div>
            <div style={S.resumeSub}>You were at {fmtClock(resumePrompt.pos)}</div>
            <div style={S.resumeBtns}>
              <button
                style={S.resumeBtn}
                onClick={() => { const { show, pos } = resumePrompt; setResumePrompt(null); startShow(show, pos); }}
              >
                CONTINUE
              </button>
              <button
                style={S.resumeBtn}
                onClick={() => { const { show } = resumePrompt; setResumePrompt(null); clearHistory(show.date); startShow(show, null); }}
              >
                START OVER
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================== PLAYER OVERLAY ================== */}
      {playerSrc && (
        <div style={S.overlay}>
          <iframe
            ref={playerFrameRef}
            title="VOD Player"
            src={`${PLAYER}?ui=tv&src=${encodeURIComponent(playerSrc)}`}
            allow="autoplay; fullscreen"
            allowFullScreen
            style={S.overlayFrame}
          />

          {/* full-screen tap layer: shows/hides controls */}
          <div
            style={S.tapLayer}
            onClick={() => (showCtl ? setShowCtl(false) : pokeCtl())}
          />

          {/* skip indicator */}
          {skipFlash && (
            <div key={skipFlash.id} className="vodSkipFlash" style={S.skipFlash}>
              {skipFlash.dir < 0 && (
                <svg viewBox="0 0 24 24" style={S.skipSvg} fill="white">
                  <path d="M11 18V6l-8.5 6L11 18zm.5-6l8.5 6V6l-8.5 6z" />
                </svg>
              )}
              <span style={S.skipText}>{skipFlash.dir > 0 ? "+30" : "-30"}</span>
              {skipFlash.dir > 0 && (
                <svg viewBox="0 0 24 24" style={S.skipSvg} fill="white">
                  <path d="M13 6v12l8.5-6L13 6zm-.5 6L4 6v12l8.5-6z" />
                </svg>
              )}
            </div>
          )}

          {showCtl && (
            <>
              {/* center cluster: -30, play/pause, +30 */}
              <div style={S.centerCluster}>
                <button style={S.roundBtn} onClick={() => skip(-1)}>
                  <svg viewBox="0 0 24 24" style={{ width: 34, height: 34 }} fill="white">
                    <path d="M11 18V6l-8.5 6L11 18zm.5-6l8.5 6V6l-8.5 6z" />
                  </svg>
                  <span style={S.roundBtnLabel}>30</span>
                </button>
                <button style={S.bigPlayBtn} onClick={togglePlay}>
                  {isPaused ? (
                    <svg viewBox="0 0 24 24" style={{ width: 54, height: 54 }} fill="white">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" style={{ width: 54, height: 54 }} fill="white">
                      <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                    </svg>
                  )}
                </button>
                <button style={S.roundBtn} onClick={() => skip(1)}>
                  <svg viewBox="0 0 24 24" style={{ width: 34, height: 34 }} fill="white">
                    <path d="M13 6v12l8.5-6L13 6zm-.5 6L4 6v12l8.5-6z" />
                  </svg>
                  <span style={S.roundBtnLabel}>30</span>
                </button>
              </div>

              {/* bottom bar: time + scrubbable progress + time */}
              {vodTime.dur > 0 && (
                <div style={S.progWrap}>
                  <span style={S.progTime}>{fmtClock(barCur)}</span>
                  <input
                    type="range"
                    min={0}
                    max={Math.floor(vodTime.dur)}
                    value={Math.floor(barCur)}
                    onChange={(e) => updateDrag(parseInt(e.target.value, 10))}
                    onPointerUp={(e) => commitSeek(parseInt(e.target.value, 10))}
                    onTouchEnd={(e) => { if (dragT !== null) commitSeek(dragT); }}
                    className="vodScrub"
                    style={S.scrub}
                  />
                  <span style={S.progTime}>{fmtClock(vodTime.dur)}</span>
                </div>
              )}

            </>
          )}

          {/* right stack: volume + exit — ALWAYS visible */}
          <div style={S.rightStack}>
            <div style={S.volGroup}>
              <button style={S.stackBtn} onClick={() => changeVol(10)}>VOL +</button>
              <div style={S.volLabel}>{vol}%</div>
              <button style={S.stackBtn} onClick={() => changeVol(-10)}>VOL −</button>
            </div>
            <button style={S.stackBtn} onClick={closePlayer}>EXIT</button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes vodChase{
          0%,100%{ background:radial-gradient(circle at 38% 32%, #ffe39a, #f7c04a 55%, #9a5a14 100%); box-shadow:0 0 10px 3px rgba(255,227,154,.95); }
          33%,66%{ background:radial-gradient(circle at 38% 32%, #b87e34, #7a4314 60%, #4e2a0e); box-shadow:0 0 3px 1px rgba(247,192,74,.25); }
        }
        @keyframes vodFlicker{
          0%, 86%, 100%{ opacity:1; }
          87%{ opacity:.45; } 88%{ opacity:1; }
          90%{ opacity:.65; } 91%{ opacity:1; }
          93%{ opacity:.35; } 93.6%{ opacity:1; }
          96%{ opacity:.8; } 96.5%{ opacity:1; }
        }
        @keyframes vodSkipFade{
          0%{ opacity:0; transform:translate(-50%,-50%) scale(.85); }
          15%{ opacity:1; transform:translate(-50%,-50%) scale(1); }
          70%{ opacity:1; }
          100%{ opacity:0; transform:translate(-50%,-50%) scale(1.05); }
        }
        .vodBulb{ animation: vodChase 1.1s steps(1,end) infinite; }
        .vodNeon{ animation: vodFlicker 5s infinite steps(60); }
        .vodSkipFlash{ animation: vodSkipFade .8s ease forwards; }
        /* big touch-friendly scrubber */
        .vodScrub{ -webkit-appearance:none; appearance:none; background:transparent; }
        .vodScrub::-webkit-slider-runnable-track{
          height:14px; border-radius:8px; background:rgba(255,255,255,0.3);
        }
        .vodScrub::-webkit-slider-thumb{
          -webkit-appearance:none; appearance:none; width:38px; height:38px; border-radius:50%;
          background:#f7c04a; border:3px solid #fff; margin-top:-12px;
          box-shadow:0 2px 10px rgba(0,0,0,.6);
        }
      `}</style>
    </>
  );
};

/* ---------- styles (1280x800 proportions) ---------- */
const S = {
  stage: {
    position: "absolute", top: 0, left: 0, width: "100vw", height: "100vh",
    backgroundColor: "#3f1305",
    backgroundImage: "url('/brickdisplay.jpg')",
    backgroundSize: "cover", backgroundPosition: "center",
    color: "#fff", fontFamily: "Lato, Arial, sans-serif",
    display: "flex", flexDirection: "column", overflow: "hidden", zIndex: 2500,
  },
  amber: {
    position: "absolute", inset: 0, pointerEvents: "none", zIndex: 2501,
    background:
      "radial-gradient(52% 42% at 50% 6%, rgba(255,176,84,.22) 0%, transparent 62%)," +
      "radial-gradient(42% 52% at 12% 32%, rgba(255,150,60,.10) 0%, transparent 66%)," +
      "radial-gradient(42% 52% at 88% 36%, rgba(255,150,60,.09) 0%, transparent 66%)",
    mixBlendMode: "screen",
  },
  header: { display: "flex", alignItems: "center", padding: "22px 40px 4px" },
  wts: {
    fontWeight: 900, fontSize: 40, letterSpacing: 2, color: "#eafaff",
    textShadow: "0 0 4px #fff, 0 0 8px #bdecff, 0 0 16px #39b6ff, 0 0 30px #39b6ff, 0 0 46px rgba(57,182,255,.5)",
  },
  live: {
    fontFamily: "Pacifico, cursive", fontSize: 34, color: "#ff5454", marginLeft: 7,
    transform: "rotate(-6deg)", display: "inline-block",
    textShadow: "0 0 5px rgba(255,84,84,.9), 0 0 13px rgba(255,40,40,.6)",
  },
  heroWrap: {
    position: "relative", margin: "16px 40px 0", padding: 14, borderRadius: 16,
    background: "#2a0c04",
    boxShadow: "0 18px 50px rgba(0,0,0,.6), inset 0 0 0 2px rgba(0,0,0,.5), 0 0 44px 6px rgba(255,165,70,.16)",
  },
  hero: {
    position: "relative", zIndex: 1, height: 372, display: "flex", alignItems: "center",
    padding: 38, borderRadius: 8, overflow: "hidden",
  },
  heroBg: { position: "absolute", inset: 0, backgroundSize: "cover", backgroundPosition: "center" },
  heroScrim: {
    position: "absolute", inset: 0,
    background:
      "linear-gradient(180deg, rgba(30,8,2,.3) 0%, rgba(30,8,2,.1) 40%, rgba(30,8,2,.7) 90%, rgba(30,8,2,.85) 100%)," +
      "linear-gradient(90deg, rgba(30,8,2,.9) 0%, rgba(30,8,2,.3) 55%, transparent 100%)",
  },
  heroInner: { position: "relative", maxWidth: 560, transform: "translateY(-16px)" },
  eyebrow: {
    display: "block", marginBottom: 8, fontSize: 18, letterSpacing: 4,
    textTransform: "uppercase", color: "#f7c04a", fontWeight: 900,
  },
  heroH1: {
    fontWeight: 900, fontSize: 46, lineHeight: 1, margin: "0 0 26px", letterSpacing: -1,
    textShadow: "0 2px 16px rgba(0,0,0,.55)",
  },
  btnWatch: {
    display: "inline-flex", alignItems: "center", gap: 12, cursor: "pointer",
    background: "#f7c04a", color: "#3a1500", border: "none", borderRadius: 999,
    padding: "14px 30px", fontFamily: "Lato", fontSize: 18, fontWeight: 900,
  },
  heroMarquee: { position: "absolute", inset: 0, pointerEvents: "none", zIndex: 4 },
  bulb: {
    position: "absolute", width: 11, height: 11, margin: "-5.5px 0 0 -5.5px", borderRadius: "50%",
    background: "radial-gradient(circle at 38% 32%, #ffe39a 0%, #f7c04a 55%, #9a5a14 100%)",
    boxShadow: "0 0 6px 2px rgba(247,192,74,.55)",
  },
  past: { padding: "24px 0 0 32px", flex: 1, minHeight: 0 },
  pastH2: { fontWeight: 900, fontSize: 24, margin: "0 0 14px" },
  row: {
    display: "flex", gap: 16, overflowX: "auto", overflowY: "hidden",
    padding: "6px 40px 12px 12px", scrollBehavior: "smooth",
    WebkitOverflowScrolling: "touch",
  },
  card: {
    position: "relative", flex: "0 0 248px", width: 248,
    borderRadius: 11, overflow: "hidden", cursor: "pointer",
    boxShadow: "0 10px 26px rgba(0,0,0,.55)",
    backgroundColor: "#1a0600",
  },
  cardImg: {
    width: "100%", aspectRatio: "16/9",
    backgroundSize: "cover", backgroundPosition: "center",
  },
  cardPlate: {
    padding: "8px 12px 9px", textAlign: "center",
    background: "#1c0902", borderTop: "1px solid rgba(255,255,255,.08)",
    color: "#fff", fontSize: 17, fontWeight: 900, fontFamily: "Lato",
    letterSpacing: .4, whiteSpace: "nowrap",
  },
  exitBtn: {
    position: "absolute", right: 40, bottom: 18, zIndex: 2600,
    background: "rgba(0,0,0,0.35)", border: "2px solid white", borderRadius: 8,
    color: "white", fontSize: 22, fontWeight: 700, fontFamily: "Lato",
    height: 50, padding: "0 24px", cursor: "pointer",
  },
  /* ---------- player overlay ---------- */
  overlay: { position: "absolute", inset: 0, width: "100vw", height: "100vh", backgroundColor: "#000", zIndex: 3000 },
  overlayFrame: { position: "absolute", inset: 0, width: "100vw", height: "100vh", border: "none", pointerEvents: "none" },
  tapLayer: { position: "absolute", inset: 0, zIndex: 3050 },
  centerCluster: {
    position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
    zIndex: 3100, display: "flex", alignItems: "center", gap: 44,
  },
  bigPlayBtn: {
    width: 110, height: 110, borderRadius: "50%", cursor: "pointer",
    background: "rgba(0,0,0,0.55)", border: "3px solid white",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  roundBtn: {
    width: 84, height: 84, borderRadius: "50%", cursor: "pointer",
    background: "rgba(0,0,0,0.55)", border: "2px solid white",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    gap: 2,
  },
  roundBtnLabel: { color: "white", fontFamily: "Lato", fontWeight: 900, fontSize: 16 },
  progWrap: {
    position: "absolute", left: "50%", bottom: 96, transform: "translateX(-50%)",
    zIndex: 3100, display: "flex", alignItems: "center", gap: 16,
    background: "rgba(0,0,0,0.6)", borderRadius: 14, padding: "12px 20px",
    width: "78vw",
  },
  scrub: { flex: 1, height: 38, cursor: "pointer" },
  progTime: {
    color: "white", fontFamily: "Lato", fontWeight: 700, fontSize: 20,
    fontVariantNumeric: "tabular-nums", minWidth: 78, textAlign: "center",
  },
  rightStack: {
    position: "absolute", right: 22, bottom: 18, zIndex: 3100,
    display: "flex", flexDirection: "column", gap: 10, alignItems: "stretch",
    width: 130,
  },
  volGroup: {
    display: "flex", flexDirection: "column", gap: 8, alignItems: "stretch",
  },
  volLabel: {
    color: "white", fontFamily: "Lato", fontWeight: 700, fontSize: 18,
    textAlign: "center", background: "rgba(0,0,0,0.45)", borderRadius: 8,
    padding: "4px 0",
  },
  stackBtn: {
    background: "rgba(0,0,0,0.55)", border: "2px solid white", borderRadius: 10,
    color: "white", fontSize: 20, fontWeight: 700, fontFamily: "Lato",
    height: 52, width: "100%", textAlign: "center", cursor: "pointer", outline: "none",
  },
  skipFlash: {
    position: "absolute", left: "50%", top: "28%", transform: "translate(-50%,-50%)",
    zIndex: 3200, display: "flex", alignItems: "center", gap: 14,
    background: "rgba(0,0,0,0.55)", borderRadius: 16, padding: "14px 26px",
    pointerEvents: "none",
  },
  skipSvg: { width: 44, height: 44 },
  skipText: {
    color: "white", fontFamily: "Lato", fontWeight: 900, fontSize: 38, letterSpacing: 1,
    textShadow: "0 2px 12px rgba(0,0,0,.8)",
  },
  resumeWrap: {
    position: "absolute", inset: 0, zIndex: 2950,
    background: "rgba(0,0,0,0.75)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  resumeBox: {
    background: "#1c0902", border: "2px solid rgba(255,255,255,.25)", borderRadius: 16,
    padding: "32px 44px", textAlign: "center",
    boxShadow: "0 24px 60px rgba(0,0,0,.7)",
  },
  resumeTitle: { fontSize: 30, fontWeight: 900, fontFamily: "Lato", color: "#fff", marginBottom: 8 },
  resumeSub: { fontSize: 19, fontFamily: "Lato", color: "#e8c5a8", marginBottom: 24 },
  resumeBtns: { display: "flex", gap: 16, justifyContent: "center" },
  resumeBtn: {
    background: "rgba(0,0,0,0.5)", border: "2px solid white", borderRadius: 10, color: "white",
    fontSize: 20, fontWeight: 700, fontFamily: "Lato",
    height: 56, width: 190, textAlign: "center", cursor: "pointer", outline: "none",
  },
  empty: { textAlign: "center", padding: "80px 20px", color: "#e8c5a8" },
  emptyH: { fontSize: 34, color: "#fff", fontWeight: 900, margin: "0 0 10px" },
};

export default VodGalleryDisplay;
